import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PAYMENT_API = '/PaymentChannel';

// Transform payment data to match AuthNet API expected format
const transformPaymentData = (paymentDetails) => {
  const isACH = paymentDetails.paymentType === 'ACH';
  
  // Debug: Log payment details to see address data
  console.log('Payment details received:', paymentDetails);
  console.log('Address fields:', {
    userAddress: paymentDetails.userAddress,
    streetAddress1: paymentDetails.streetAddress1,
    city: paymentDetails.city,
    state: paymentDetails.state,
    zipCode: paymentDetails.zipCode,
    country: paymentDetails.country
  });
  
  return {
    app: "Class",
    entity: paymentDetails.entity || paymentDetails.categoryType || "RMS",
    paymentType: isACH ? "ach" : "cc",
    customerEmail: paymentDetails.userEmail,
    orderid: paymentDetails.classId?.toString() || "0",
    amount: paymentDetails.amount?.toString() || "0",
    description: `Class Enrollment: ${paymentDetails.className} - ${paymentDetails.numberOfStudents} student(s) - Amount: $${paymentDetails.amount}`,
    customer: {
      firstName: paymentDetails.userName?.split(' ')[0] || '',
      lastName: paymentDetails.userName?.split(' ').slice(1).join(' ') || '',
      email: paymentDetails.userEmail,
      phone: paymentDetails.userPhone || '',
      company: "string",
      notes: `Class Enrollment for ${paymentDetails.userName} - Class: ${paymentDetails.className}`,
      customerAccount: paymentDetails.userId?.toString() || "string",
      shippingSameAsBilling: true,
      billingAddress: {
        streetAddress1: paymentDetails.userAddress || paymentDetails.streetAddress1 || "-",
        streetAddress2: paymentDetails.streetAddress2 || "-",
        city: paymentDetails.city || "-",
        stateCode: paymentDetails.state || paymentDetails.stateCode || "-",
        zipCode: paymentDetails.zipCode || paymentDetails.postalCode || "-",
        country: paymentDetails.country || "USA"
      },
      shippingAddress: {
        streetAddress1: paymentDetails.userAddress || paymentDetails.streetAddress1 || "-",
        streetAddress2: paymentDetails.streetAddress2 || "-",
        city: paymentDetails.city || "-",
        stateCode: paymentDetails.state || paymentDetails.stateCode || "-",
        zipCode: paymentDetails.zipCode || paymentDetails.postalCode || "-",
        country: paymentDetails.country || "USA"
      }
    },
    creditcard: isACH ? {
      creditCardNumber: "string",
      expirationDate: "string",
      issuer: "string",
      billingZipCode: "string",
      customerId: "",
      cvv: "string"
    } : {
      creditCardNumber: paymentDetails.cardNumber,
      expirationDate: paymentDetails.expiryDate,
      issuer: "string",
      billingZipCode: paymentDetails.zipCode || paymentDetails.postalCode || "-",
      customerId: "",
      cvv: paymentDetails.cvv
    },
    ach: isACH ? {
      isCheckingAccount: paymentDetails.accountType === 'checking' ? "Y" : "N",
      routingNumber: paymentDetails.routingNumber,
      accountNumber: paymentDetails.accountNumber,
      bankName: paymentDetails.bankName || "Financial Institution",
      customerId: "",
      isDefault: "false"
    } : {
      isCheckingAccount: "string",
      routingNumber: "string",
      accountNumber: "string",
      bankName: "string",
      customerId: "string",
      isDefault: "string"
    },
    recurr: {
      paymentAmount: "string",
      accountId: "string",
      startDate: "string",
      executionFrequencyType: "string",
      executionFrequencyParameter: "string",
      endDate: "string",
      paymentSubType: "string",
      invoiceNumber: "string",
      firstPaymentAmount: "string",
      firstPaymentDate: "string",
      description: "string"
    }
  };
};

const paymentService = {
  // Process One-Time Payment (Credit Card or ACH) via Authorize.Net
  processOneTimePayment: async (paymentData) => {
    try {
      const transformedData = transformPaymentData(paymentData);
      const response = await axios.post(`${API_BASE_URL}${PAYMENT_API}/AuthNetOneTimePayment`, transformedData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Payment processing error:', error?.response?.data || error);
      throw error;
    }
  },

  // Send Payment Confirmation Email
  sendPaymentConfirmationEmail: async (emailData) => {
    try {
      // Format student names list
      const studentsList = emailData.students && emailData.students.length > 0
        ? emailData.students.map(s => {
            const firstName = s.student_First_Name || s.firstName || '';
            const lastName = s.student_Last_Name || s.lastName || '';
            return `${firstName} ${lastName}`.trim();
          }).join(', ')
        : 'N/A';
      
      // Email configuration (From, Subject, Body) will come from App_Email_Configuration table
      // based on templateName=YKClassPayment, so we use placeholder 'string' values
      const emailParameter = {
        emailFrom: 'string',
        emailTo: emailData.toEmail || 'string',
        emailSubject: 'string',
        emailBody: 'string',
        attachment: 'Y',
        userPhoneNumber: emailData.userPhone || 'string',
        location: 'YKClassPayment',
        input: 'string',
        appContent: {
          userName: emailData.userName || 'string',
          programName: emailData.className || 'string',
          appSchedule: emailData.classDateRange || 'string',
          attendee: studentsList, // Send student names instead of count for ##Students## placeholder
          studentNames: studentsList,
          date: emailData.transactionDate || new Date().toLocaleString(),
          orderType: 'Class Enrollment',
          orderNo: emailData.transactionId || 'string',
          paymentMode: emailData.paymentMethod || 'string',
          amount: String(emailData.totalAmount || 0),
          email: emailData.toEmail || 'string',
          phone: emailData.userPhone || 'string',
          pickupPerson: emailData.userPhone || 'string', // Send phone here too in case template uses this
          pickupPersonContact: emailData.userPhone || 'string',
          instructions: emailData.classDateRange || 'string', // Send class date here too
          itemData: studentsList,
          pickUpDate: emailData.userPhone || 'string', // Template might be using this for phone
          pickUpTime: emailData.classDateRange || 'string', // Template might be using this for class date
          paymentType: emailData.paymentMethod || 'string',
          billingAddress: emailData.billingAddress || 'string',
          address: emailData.billingAddress || 'string',
          pickupOrReceiver: emailData.classDateRange || 'string',
          orderReady: String(emailData.totalAmount || 0),
          orderReadyLastLine: 'string',
          headingDate: emailData.classDateRange || 'string',
          headingTime: emailData.userPhone || 'string',
          startDate: new Date().toISOString(),
          endDate: new Date().toISOString()
        }
      };
      
      console.log('Email parameter being sent:', JSON.stringify(emailParameter, null, 2));
      
      const response = await axios.post(
        `${API_BASE_URL}/MessageChannel/SendEmailMessage?appName=Class&templateName=YKClassPayment&orgId=3`,
        emailParameter
      );
      return response.data;
    } catch (error) {
      console.error('Email sending error:', error?.response?.data || error);
      throw error;
    }
  },

  // Create Class Enrollment Record
  createClassEnrollment: async (enrollmentData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/CommonChannel/CreateClassEnrollment`, enrollmentData);
      return response.data;
    } catch (error) {
      console.error('Enrollment creation error:', error);
      throw error;
    }
  },

  // Create Payment Record - Returns Payment_Details_Id
  createPaymentRecord: async (paymentData) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Class/PostPayment`, paymentData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Payment record creation error:', error);
      throw error;
    }
  },

  // Create Class Order Record
  createClassOrder: async (orderData) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Class/PostClassOrder`, orderData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Order creation error:', error);
      throw error;
    }
  },

  // Create Class Enrollment and Order for a student
  createClassEnrollmentAndOrder: async (enrollmentData) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Class/PostClassEnrollmentAndOrder`, enrollmentData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Enrollment and order creation error:', error);
      throw error;
    }
  },

  // Update Order Status after payment
  updateOrderStatus: async (updateData) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Class/UpdateOrder`, updateData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Order update error:', error);
      throw error;
    }
  }
};

export default paymentService;
